/**
 * Hashtag chips for one variant.
 *
 * Connectors publish the caption verbatim, so a tag only ships if it is in the
 * caption. Tags the model suggested but never wrote in are shown dimmed with an
 * "insert" affordance rather than silently dropped.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Hash, Plus, X } from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId } from '../../../shared/types';
import { normalizeHashtag } from '../../../shared/utils';
import { Button, Tooltip } from '../ui';
import { cn } from '../../lib/utils';

export interface HashtagEditorProps {
  platform: PlatformId;
  tags: string[];
  /** Subset of `tags` that is missing from the caption. */
  missing: string[];
  onAdd: (tags: string[]) => void;
  onRemove: (tag: string) => void;
  disabled?: boolean;
  /** AI suggestion button, rendered inline with the chips. */
  action?: ReactNode;
  className?: string;
}

export function HashtagEditor({
  platform,
  tags,
  missing,
  onAdd,
  onRemove,
  disabled,
  action,
  className,
}: HashtagEditorProps) {
  const [draft, setDraft] = useState('');
  const spec = PLATFORM_SPECS[platform];
  const missingSet = new Set(missing.map((tag) => tag.toLowerCase()));
  const overLimit = tags.length > spec.limits.maxHashtags;

  const commit = () => {
    const normalized = draft
      .split(/[\s,]+/)
      .map((entry) => normalizeHashtag(entry))
      .filter(Boolean);
    if (normalized.length) onAdd(normalized);
    setDraft('');
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => {
          const notInCaption = missingSet.has(tag.toLowerCase());
          return (
            <span
              key={tag}
              className={cn(
                'group inline-flex items-center gap-1 rounded-full border py-0.5 pl-2 pr-1 text-xs',
                notInCaption
                  ? 'border-dashed border-border text-subtle'
                  : 'border-accent/30 bg-accent-soft text-accent',
              )}
            >
              {notInCaption ? (
                <Tooltip
                  content={
                    spec.features.firstComment
                      ? 'Not in the caption or first comment yet — click to insert it'
                      : 'Not in the caption yet — click to insert it'
                  }
                >
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onAdd([tag])}
                    className="inline-flex items-center gap-1"
                  >
                    <Plus className="size-3" aria-hidden />
                    {tag}
                  </button>
                </Tooltip>
              ) : (
                <span>{tag}</span>
              )}
              <button
                type="button"
                disabled={disabled}
                onClick={() => onRemove(tag)}
                aria-label={`Remove ${tag}`}
                className="rounded-full p-0.5 text-current opacity-60 transition-opacity hover:opacity-100"
              >
                <X className="size-3" aria-hidden />
              </button>
            </span>
          );
        })}

        <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 focus-within:border-accent">
          <Hash className="size-3 text-subtle" aria-hidden />
          <input
            value={draft}
            disabled={disabled}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
                event.preventDefault();
                commit();
              }
              if (event.key === 'Backspace' && !draft && tags.length) {
                const last = tags[tags.length - 1];
                if (last) onRemove(last);
              }
            }}
            placeholder="add"
            aria-label={`Add a hashtag for ${spec.shortName}`}
            className="w-16 bg-transparent text-xs text-fg outline-none placeholder:text-subtle"
          />
        </span>

        {action}
      </div>

      <p
        className={cn(
          'text-[11px]',
          overLimit ? 'text-danger' : missing.length ? 'text-warning' : 'text-subtle',
        )}
      >
        {overLimit
          ? `${spec.shortName} allows at most ${spec.limits.maxHashtags}.`
          : missing.length
            ? `${missing.length} tag${missing.length === 1 ? '' : 's'} not in the ${spec.features.firstComment ? 'caption or first comment' : 'caption'} — ${missing.length === 1 ? 'it will' : 'they will'} not publish.`
            : `${spec.shortName} rewards about ${spec.limits.recommendedHashtags}.`}
        {missing.length > 0 && !overLimit && (
          <Button
            variant="link"
            size="xs"
            className="ml-2 align-baseline"
            disabled={disabled}
            onClick={() => onAdd(missing)}
          >
            Add them to the caption
          </Button>
        )}
      </p>
    </div>
  );
}
